// Phase 9 — NotificationBell + dropdown panel. Lives in top nav.
import { useEffect, useState, useCallback, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtRelative(dt) {
    if (!dt) return "";
    const d = typeof dt === "string" ? new Date(dt) : dt;
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

const TYPE_ICONS = {
    analysis_complete: "✓",
    validation_assigned: "◌",
    outcome_recorded: "↳",
    regulator_accessed_link: "⌖",
    branch_ready: "✓",
    branch_failed: "!",
    calibration_state_changed: "≈",
    analysis_verified: "⌖",
    outcome_prompt_due: "◷",
};

export default function NotificationBell() {
    const { user } = useAuth() || {};
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const fetchAll = useCallback(async () => {
        if (!user) {
            setItems([]); setUnread(0); return;
        }
        try {
            const r = await fetch(`${API}/api/notifications`, { credentials: "include" });
            if (!r.ok) return;
            const data = await r.json();
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch (_) {
            // silent — bell is non-critical
        }
    }, [user]);

    useEffect(() => {
        fetchAll();
        if (!user) return;
        // Poll only while the tab is visible (mirror of AdminPortal LiveSignIns gate).
        const tick = () => {
            if (document.visibilityState === "visible") fetchAll();
        };
        const t = setInterval(tick, 30_000);
        // Refresh immediately when the tab regains focus.
        const onVis = () => { if (document.visibilityState === "visible") fetchAll(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(t);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [user, fetchAll]);

    useEffect(() => {
        function onClickAway(e) {
            if (!wrapperRef.current?.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [open]);

    if (!user) return null;

    const markRead = async (id) => {
        await fetch(`${API}/api/notifications/${id}/read`, { method: "POST", credentials: "include" });
        fetchAll();
    };
    const markAll = async () => {
        await fetch(`${API}/api/notifications/read-all`, { method: "POST", credentials: "include" });
        fetchAll();
    };

    return (
        <div ref={wrapperRef} className="relative" data-testid="notification-bell-wrap">
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label="Notifications"
                data-testid="notification-bell"
                className="relative inline-flex items-center justify-center w-9 h-9 text-ink-soft hover:text-ink transition-colors"
            >
                <Bell size={17} />
                {unread > 0 && (
                    <span
                        data-testid="notification-unread-dot"
                        className="absolute top-1.5 right-1.5 inline-block w-2 h-2 rounded-full bg-amber-accent ring-2 ring-paper"
                    />
                )}
            </button>
            {open && (
                <div
                    data-testid="notification-panel"
                    className="absolute right-0 mt-2 w-[360px] max-h-[440px] overflow-y-auto bg-paper border border-ink/15 shadow-xl z-50"
                >
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink/10 sticky top-0 bg-paper">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                            Notifications {unread > 0 && <span className="text-amber-accent">· {unread} new</span>}
                        </span>
                        {unread > 0 && (
                            <button
                                onClick={markAll}
                                data-testid="notifications-mark-all-read"
                                className="text-[11px] text-ink-soft hover:text-ink font-mono"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>
                    {items.length === 0 ? (
                        <div className="px-4 py-6 text-center text-[13px] text-ink-soft">
                            Nothing yet.
                        </div>
                    ) : (
                        <ul>
                            {items.map((n) => (
                                <li key={n.id} className={`border-b border-ink/[0.06] last:border-b-0 ${!n.read_at ? "bg-amber-soft/30" : ""}`}>
                                    <div className="flex gap-3 px-4 py-3">
                                        <span className="text-[14px] text-amber-accent shrink-0 font-mono mt-0.5">
                                            {TYPE_ICONS[n.type] || "·"}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            {n.link ? (
                                                <Link
                                                    to={n.link}
                                                    onClick={() => { setOpen(false); markRead(n.id); }}
                                                    className="block text-[13px] text-ink font-medium hover:text-amber-accent"
                                                >
                                                    {n.title}
                                                </Link>
                                            ) : (
                                                <span className="block text-[13px] text-ink font-medium">{n.title}</span>
                                            )}
                                            {n.body && (
                                                <span className="block text-[12px] text-ink-soft mt-0.5 leading-snug">{n.body}</span>
                                            )}
                                            <span className="block text-[10px] font-mono text-ink-soft/70 uppercase tracking-[0.14em] mt-1">
                                                {fmtRelative(n.created_at)}
                                            </span>
                                        </div>
                                        {!n.read_at && (
                                            <button
                                                onClick={() => markRead(n.id)}
                                                aria-label="Mark read"
                                                className="text-ink-soft/70 hover:text-ink shrink-0"
                                                data-testid="notification-mark-read"
                                            >
                                                <Check size={13} />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
